import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Settings as SettingsIcon, Moon, Sun, UtensilsCrossed, Edit, Trash2, Plus, Clock, Users, Globe, LogOut, Armchair } from 'lucide-react';
import { useDarkMode, type Theme } from '../hooks/useDarkMode';
import { Button } from '../components/ui/Button';
import toast from 'react-hot-toast';
import localDatabase from '../services/localDatabase';
import type { Table, Shift } from '../types';
import TableForm, { TableFormValues } from '../components/settings/TableForm';
import ShiftForm, { ShiftFormValues } from '../components/settings/ShiftForm';
import { publishCrossTab } from '../services/crossTab';
import { pushPendingToSupabase } from '../services/supabaseSync';
import {
  getMaxTableSeats,
  getMaxTablesOccupied,
  setMaxTableSeats,
  setMaxTablesOccupied,
} from '../services/restaurantSettings';
import { useTenant } from '../contexts/TenantContext';
import { tenantService } from '../services/supabaseService';
import { APP_NAME, APP_VERSION } from '../lib/config'
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const Settings: React.FC = () => {
  const { setTheme, isDark } = useDarkMode();
  const { tenantId } = useTenant();
  const { signOut, user } = useAuth();
  const navigate = useNavigate();
  const [tables, setTables] = useState<Table[]>([]);
  const [tablesLoading, setTablesLoading] = useState(false);
  const [tableModalOpen, setTableModalOpen] = useState(false);
  const [tableSubmitting, setTableSubmitting] = useState(false);
  const [editingTable, setEditingTable] = useState<Table | null>(null);
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [shiftsLoading, setShiftsLoading] = useState(false);
  const [shiftModalOpen, setShiftModalOpen] = useState(false);
  const [shiftSubmitting, setShiftSubmitting] = useState(false);
  const [editingShift, setEditingShift] = useState<Shift | null>(null);
  
  // Capacity settings
  const [maxTablesOccupied, setMaxTablesOccupiedState] = useState<number | null>(() => getMaxTablesOccupied());
  const [maxTableSeats, setMaxTableSeatsState] = useState<number>(() => getMaxTableSeats());
  
  // Online booking: capacity percentage for online reservations (0-100, null = unlimited)
  const [onlineBookingCapacityPercent, setOnlineBookingCapacityPercent] = useState<number | null>(null);
  const [maxOnlinePartySize, setMaxOnlinePartySize] = useState<number | null>(null);
  const [onlineBookingLoading, setOnlineBookingLoading] = useState(false);
  const [onlineBookingSaving, setOnlineBookingSaving] = useState(false);
  const [maxOnlinePartySizeSaving, setMaxOnlinePartySizeSaving] = useState(false);

  const handleLogout = async () => {
    try {
      await signOut();
      toast.success('Αποσυνδέθηκε επιτυχώς');
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      toast.error('Σφάλμα κατά την αποσύνδεση');
    }
  };
  
  const handleMaxTablesChange = (value: string) => {
    const num = parseInt(value, 10);
    if (value === '' || isNaN(num) || num <= 0) {
      setMaxTablesOccupiedState(null);
      setMaxTablesOccupied(null);
      toast.success('Το όριο χωρητικότητας απενεργοποιήθηκε');
    } else {
      setMaxTablesOccupiedState(num);
      setMaxTablesOccupied(num);
      toast.success(`Το όριο χωρητικότητας ορίστηκε σε ${num} τραπέζια`);
    }
  };

  const handleMaxTableSeatsChange = (value: string) => {
    const num = parseInt(value, 10);
    if (!Number.isFinite(num) || num < 1) return;
    setMaxTableSeatsState(num);
    setMaxTableSeats(num);
    toast.success(`Το μέγιστο όριο ανά τραπέζι ορίστηκε σε ${num} θέσεις`);
  };

  const loadTables = useCallback(async () => {
    setTablesLoading(true);
    try {
      const records = await localDatabase.getAllTables();
      setTables(records as unknown as Table[]);
    } catch (error) {
      if (error instanceof Error && error.message.includes('Database not initialized')) {
        // Silently handle - database not ready yet
        setTables([]);
      } else {
        console.error('Failed to fetch tables', error);
        toast.error('Αποτυχία φόρτωσης τραπεζιών.');
      }
    } finally {
      setTablesLoading(false);
    }
  }, []);

  const loadShifts = useCallback(async () => {
    setShiftsLoading(true);
    try {
      const records = await localDatabase.getAllShifts();
      setShifts(records as unknown as Shift[]);
    } catch (error) {
      if (error instanceof Error && error.message.includes('Database not initialized')) {
        // Silently handle - database not ready yet
        setShifts([]);
      } else {
        console.error('Failed to fetch shifts', error);
        toast.error('Αποτυχία φόρτωσης βάρδιας.');
      }
    } finally {
      setShiftsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTables();
    loadShifts();
  }, [loadTables, loadShifts]);

  const loadOnlineBookingSetting = useCallback(async () => {
    if (!tenantId) return;
    setOnlineBookingLoading(true);
    try {
      const tenant = await tenantService.getTenant(tenantId);
      setOnlineBookingCapacityPercent(tenant?.online_booking_capacity_percent ?? null);
      setMaxOnlinePartySize(tenant?.max_online_party_size ?? null);
    } catch (error) {
      console.error('Failed to load online booking setting', error);
      toast.error('Αποτυχία φόρτωσης ρύθμισης online κρατήσεων.');
    } finally {
      setOnlineBookingLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    loadOnlineBookingSetting();
  }, [loadOnlineBookingSetting]);

  const handleOnlineBookingCapacityPercentChange = async (value: string) => {
    if (!tenantId) {
      toast.error('Δεν έχει επιλεγεί επιχείρηση.');
      return;
    }
    const num = value === '' ? null : parseInt(value, 10);
    if (value !== '' && (isNaN(num!) || num! < 0 || num! > 100)) return;
    const percent = num != null && num >= 0 && num <= 100 ? num : null;
    setOnlineBookingSaving(true);
    try {
      await tenantService.updateTenant(tenantId, { online_booking_capacity_percent: percent });
      setOnlineBookingCapacityPercent(percent);
      toast.success(
        percent != null
          ? `Οι online κρατήσεις περιορίζονται στο ${percent}% της χωρητικότητας`
          : 'Χωρίς όριο για online κρατήσεις'
      );
    } catch (error) {
      console.error('Failed to save online booking setting', error);
      toast.error('Αποτυχία αποθήκευσης ρύθμισης.');
    } finally {
      setOnlineBookingSaving(false);
    }
  };

  const handleMaxOnlinePartySizeChange = async (value: string) => {
    if (!tenantId) {
      toast.error('Δεν έχει επιλεγεί επιχείρηση.');
      return;
    }

    const trimmed = value.trim();
    const num = trimmed === '' ? null : parseInt(trimmed, 10);
    if (trimmed !== '' && (!Number.isFinite(num) || num! < 1)) return;

    const nextValue = num != null && num >= 1 ? num : null;
    setMaxOnlinePartySizeSaving(true);
    try {
      await tenantService.updateTenant(tenantId, { max_online_party_size: nextValue });
      setMaxOnlinePartySize(nextValue);
      toast.success(
        nextValue != null
          ? `Το online όριο ατόμων ορίστηκε στα ${nextValue}`
          : 'Το online όριο ατόμων επέστρεψε στην προεπιλογή των 10'
      );
    } catch (error) {
      console.error('Failed to save max online party size', error);
      toast.error('Αποτυχία αποθήκευσης ορίου online ατόμων.');
    } finally {
      setMaxOnlinePartySizeSaving(false);
    }
  };

  const zoneSuggestions = useMemo(
    () => Array.from(new Set(tables.map((table) => table.zone))).sort(),
    [tables]
  );

  const zonesWithCounts = useMemo(
    () => {
      const map = new Map<string, number>();
      tables.forEach((table) => {
        const zone = (table.zone || '').trim() || '—';
        map.set(zone, (map.get(zone) || 0) + 1);
      });
      return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
    },
    [tables]
  );

  const handleRenameZone = async (oldZone: string, newZone: string) => {
    const trimmedOld = oldZone.trim();
    const trimmedNew = newZone.trim();
    if (!trimmedNew || trimmedNew === trimmedOld) return;

    try {
      const affectedTables = tables.filter(
        (t) => (t.zone || '').trim() === trimmedOld
      );
      if (affectedTables.length === 0) {
        toast.error('Δεν βρέθηκαν τραπέζια για αυτή τη ζώνη.');
        return;
      }

      await Promise.all(
        affectedTables.map((table) =>
          localDatabase.updateTable(table.id, {
            ...table,
            zone: trimmedNew,
          } as any)
        )
      );

      toast.success(
        `Η ζώνη "${trimmedOld}" μετονομάστηκε σε "${trimmedNew}" για ${affectedTables.length} τραπέζ${
          affectedTables.length === 1 ? 'ι' : 'ια'
        }.`
      );
      await loadTables();
      publishCrossTab({ type: 'sync:tables' });
      if (navigator.onLine) {
        await pushPendingToSupabase();
      }
    } catch (error) {
      console.error('Failed to rename zone', error);
      toast.error('Αποτυχία μετονομασίας ζώνης.');
    }
  };

  const [defaultZoneForNewTable, setDefaultZoneForNewTable] = useState<string | null>(null);
  const [newZoneDraft, setNewZoneDraft] = useState('');
  const [isAddingZone, setIsAddingZone] = useState(false);
  const [zoneBeingRenamed, setZoneBeingRenamed] = useState<string | null>(null);
  const [renameZoneDraft, setRenameZoneDraft] = useState('');

  const openCreateTable = (zone?: string | null) => {
    setDefaultZoneForNewTable(zone ?? null);
    setEditingTable(null);
    setTableModalOpen(true);
  };

  const handleStartAddZone = () => {
    setIsAddingZone(true);
    setNewZoneDraft('');
  };

  const handleConfirmAddZone = () => {
    const zone = newZoneDraft.trim();
    if (!zone) {
      toast.error('Συμπληρώστε όνομα ζώνης.');
      return;
    }
    setIsAddingZone(false);
    setNewZoneDraft('');
    openCreateTable(zone);
    toast.success('Συμπληρώθηκε η νέα ζώνη στη φόρμα τραπεζιού. Ολοκληρώστε τη δημιουργία του πρώτου τραπεζιού για αυτή τη ζώνη.');
  };

  const openEditTable = (table: Table) => {
    setDefaultZoneForNewTable(null);
    setEditingTable(table);
    setTableModalOpen(true);
  };

  const closeTableModal = () => {
    setTableModalOpen(false);
    setEditingTable(null);
    setDefaultZoneForNewTable(null);
    setTableSubmitting(false);
  };

  const handleSaveTable = async (formValues: TableFormValues) => {
    setTableSubmitting(true);
    try {
      const duplicateNumber = tables.some(
        (tableRecord) =>
          tableRecord.number === formValues.number &&
          (!editingTable || tableRecord.id !== editingTable.id)
      );

      if (duplicateNumber) {
        throw new Error('Υπάρχει ήδη τραπέζι με αυτόν τον αριθμό.');
      }

      const commonUpdates = {
        number: formValues.number,
        seats: Math.min(formValues.seats, maxTableSeats),
        zone: formValues.zone,
        status: formValues.status,
        // Ensure floor-plan metadata stays in sync with the table's zone.
        floor_plan_room_id: formValues.zone,
      } as any;

      if (editingTable) {
        await localDatabase.updateTable(editingTable.id, commonUpdates);
        toast.success(`Το Τ${editingTable.number} ενημερώθηκε.`);
      } else {
        await localDatabase.createTable(
          {
            ...commonUpdates,
            // New tables appear in the middle of their zone in the floor plan.
            floor_plan_x_pct: 0.5,
            floor_plan_y_pct: 0.5,
          } as any,
        );
        toast.success('Το νέο τραπέζι δημιουργήθηκε.');
      }

      closeTableModal();
      await loadTables();
      publishCrossTab({ type: 'sync:tables' });
      if (navigator.onLine) {
        await pushPendingToSupabase();
      }
    } catch (error) {
      console.error('Failed to save table', error);
      if (!(error instanceof Error && error.message.includes('τραπέζι'))) {
        toast.error('Αποτυχία αποθήκευσης τραπεζιού.');
      }
      setTableSubmitting(false);
      throw error;
    }
  };

  const handleDeleteTable = async (table: Table) => {
    const confirmed = window.confirm(
      `Να διαγραφεί οριστικά το τραπέζι Τ${table.number};`
    );
    if (!confirmed) return;

    try {
      await localDatabase.deleteTable(table.id);
      toast.success(`Το τραπέζι Τ${table.number} διαγράφηκε.`);
      await loadTables();
      publishCrossTab({ type: 'sync:tables' });
      if (navigator.onLine) {
        await pushPendingToSupabase();
      }
    } catch (error) {
      console.error('Failed to delete table', error);
      toast.error('Αποτυχία διαγραφής τραπεζιού.');
    }
  };

  const openCreateShift = () => {
    setEditingShift(null);
    setShiftModalOpen(true);
  };

  const openEditShift = (shift: Shift) => {
    setEditingShift(shift);
    setShiftModalOpen(true);
  };

  const closeShiftModal = () => {
    setShiftModalOpen(false);
    setEditingShift(null);
    setShiftSubmitting(false);
  };

  const handleSaveShift = async (formValues: ShiftFormValues) => {
    setShiftSubmitting(true);
    try {
      if (editingShift) {
        await localDatabase.updateShift(editingShift.id, {
          name: formValues.name,
          start_time: formValues.start_time,
          end_time: formValues.end_time,
          is_active: formValues.is_active,
        } as any);
        toast.success(`Η βάρδια "${editingShift.name}" ενημερώθηκε.`);
      } else {
        await localDatabase.createShift({
          name: formValues.name,
          start_time: formValues.start_time,
          end_time: formValues.end_time,
          is_active: formValues.is_active,
        } as any);
        toast.success('Η νέα βάρδια δημιουργήθηκε.');
      }

      closeShiftModal();
      await loadShifts();
      publishCrossTab({ type: 'sync:shifts' });
      if (navigator.onLine) {
        await pushPendingToSupabase();
      }
    } catch (error) {
      console.error('Failed to save shift', error);
      toast.error('Αποτυχία αποθήκευσης βάρδιας.');
      setShiftSubmitting(false);
      throw error;
    }
  };

  const handleDeleteShift = async (shift: Shift) => {
    const confirmed = window.confirm(
      `Να διαγραφεί οριστικά η βάρδια "${shift.name}";`
    );
    if (!confirmed) return;

    try {
      await localDatabase.deleteShift(shift.id);
      toast.success(`Η βάρδια "${shift.name}" διαγράφηκε.`);
      await loadShifts();
      publishCrossTab({ type: 'sync:shifts' });
      if (navigator.onLine) {
        await pushPendingToSupabase();
      }
    } catch (error) {
      console.error('Failed to delete shift', error);
      toast.error('Αποτυχία διαγραφής βάρδιας.');
    }
  };

  const handleToggleShiftActive = async (shift: Shift) => {
    try {
      await localDatabase.updateShift(shift.id, { is_active: !shift.is_active } as any);
      toast.success(
        `Η βάρδια "${shift.name}" ${shift.is_active ? 'απενεργοποιήθηκε' : 'ενεργοποιήθηκε'}.`
      );
      await loadShifts();
      publishCrossTab({ type: 'sync:shifts' });
      if (navigator.onLine) {
        await pushPendingToSupabase();
      }
    } catch (error) {
      console.error('Failed to update shift status', error);
      toast.error('Αποτυχία ενημέρωσης βάρδιας.');
    }
  };

  const renderTableStatus = (status: Table['status']) => {
    switch (status) {
      case 'available':
        return <span className="text-emerald-600 bg-emerald-100 px-2 py-1 rounded-full text-xs font-medium">Διαθέσιμο</span>;
      case 'reserved':
        return <span className="text-blue-600 bg-blue-100 px-2 py-1 rounded-full text-xs font-medium">Κρατημένο</span>;
      case 'occupied':
        return <span className="text-red-600 bg-red-100 px-2 py-1 rounded-full text-xs font-medium">Κατειλημμένο</span>;
      default:
        return status;
    }
  };

  return (
    <div className="space-y-6 pb-10">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <SettingsIcon className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Ρυθμίσεις</h1>
            <p className="text-sm text-muted-foreground">
              Προσαρμόστε την εφαρμογή στις προτιμήσεις σας
            </p>
          </div>
        </div>
        <label className="flex items-center gap-3 cursor-pointer select-none">
          <span className="text-sm font-medium text-muted-foreground">Σκούρο θέμα</span>
          <div className="relative">
            <input
              type="checkbox"
              className="sr-only"
              checked={isDark}
              onChange={() => setTheme(isDark ? ('light' as Theme) : ('dark' as Theme))}
            />
            <div
              className={`h-6 w-11 rounded-full transition-colors duration-200 ${
                isDark ? 'bg-indigo-500' : 'bg-slate-300'
              }`}
            ></div>
            <div
              className={`absolute inset-y-1 left-1 h-4 w-4 rounded-full bg-white shadow transition-transform duration-200 ${
                isDark ? 'translate-x-5' : 'translate-x-0'
              }`}
            ></div>
          </div>
          {isDark ? <Moon className="h-4 w-4 text-indigo-500" /> : <Sun className="h-4 w-4 text-amber-500" />}
        </label>
      </div>

      {/* Settings Sections */}
      <div className="space-y-6">
        
        {/* Account / Logout */}
        <div className="bg-card border border-border rounded-lg p-6 shadow-sm flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-card-foreground">Λογαριασμός</h2>
            <p className="text-sm text-muted-foreground">
              {user?.email
                ? `Συνδεδεμένος ως ${user.email}`
                : 'Διαχείριση σύνδεσης χρήστη.'}
            </p>
          </div>
          <Button
            variant="outline"
            onClick={handleLogout}
            className="gap-2"
          >
            <LogOut className="h-4 w-4" />
            Αποσύνδεση
          </Button>
        </div>

        {/* Capacity Settings */}
        <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center">
                <Users className="h-4 w-4" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-card-foreground">Χωρητικότητα Εστιατορίου</h2>
                <p className="text-sm text-muted-foreground">
                  Ορίστε πόσα τραπέζια μπορούν να είναι κατειλημμένα ταυτόχρονα
                </p>
              </div>
            </div>
          </div>
          
          <div className="mt-4 flex items-center gap-4">
            <div className="flex-1 max-w-xs">
              <label htmlFor="maxTables" className="block text-sm font-medium text-muted-foreground mb-1">
                Μέγιστος αριθμός τραπεζιών ανά ώρα
              </label>
              <div className="flex items-center gap-2">
                <input
                  id="maxTables"
                  type="number"
                  min="0"
                  placeholder="Χωρίς όριο"
                  value={maxTablesOccupied ?? ''}
                  onChange={(e) => handleMaxTablesChange(e.target.value)}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
                <span className="text-sm text-muted-foreground whitespace-nowrap">
                  από {tables.length} συνολικά
                </span>
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                {maxTablesOccupied 
                  ? `Επιτρέπονται μέχρι ${maxTablesOccupied} κρατήσεις την ίδια ώρα`
                  : 'Αφήστε κενό για να χρησιμοποιηθούν όλα τα διαθέσιμα τραπέζια'
                }
              </p>
            </div>
          </div>
        </div>

        {/* Table seats limit */}
        <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-indigo-100 text-indigo-600 rounded-lg flex items-center justify-center">
                <Armchair className="h-4 w-4" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-card-foreground">Όριο θέσεων ανά τραπέζι</h2>
                <p className="text-sm text-muted-foreground">
                  Καθορίστε το μέγιστο πλήθος θέσεων που επιτρέπεται σε κάθε τραπέζι.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-4 flex items-center gap-4">
            <div className="flex-1 max-w-xs">
              <label htmlFor="maxTableSeats" className="block text-sm font-medium text-muted-foreground mb-1">
                Μέγιστες θέσεις ανά τραπέζι
              </label>
              <div className="flex items-center gap-2">
                <input
                  id="maxTableSeats"
                  type="number"
                  min="1"
                  step="1"
                  value={maxTableSeats}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === '') return;
                    const n = parseInt(v, 10);
                    if (!Number.isFinite(n) || n < 1) return;
                    setMaxTableSeatsState(n);
                  }}
                  onBlur={(e) => handleMaxTableSeatsChange(e.target.value)}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
                <span className="text-sm text-muted-foreground whitespace-nowrap">θέσεις</span>
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                Προεπιλογή: 10. Επηρεάζει editor floor plan και φόρμες τραπεζιών.
              </p>
            </div>
          </div>
        </div>

        {/* Online booking: max tables available for online reservation */}
        <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-emerald-100 text-emerald-600 rounded-lg flex items-center justify-center">
                <Globe className="h-4 w-4" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-card-foreground">Online κρατήσεις</h2>
                <p className="text-sm text-muted-foreground">
                  Ποσοστό χωρητικότητας που μπορεί να κλειστεί online (το υπόλοιπο διατηρείται για walk-in πελάτες)
                </p>
              </div>
            </div>
          </div>
          <div className="mt-4 grid gap-4 md:grid-cols-2">
            <div className="max-w-xs">
              <label htmlFor="onlineBookingCapacityPercent" className="block text-sm font-medium text-muted-foreground mb-1">
                Ποσοστό χωρητικότητας για online (%)
              </label>
              <div className="flex items-center gap-2">
                <input
                  id="onlineBookingCapacityPercent"
                  type="number"
                  min={0}
                  max={100}
                  placeholder="Χωρίς όριο"
                  value={onlineBookingCapacityPercent ?? ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    const num = v === '' ? null : parseInt(v, 10);
                    if (v !== '' && (isNaN(num!) || num! < 0 || num! > 100)) return;
                    setOnlineBookingCapacityPercent(v === '' ? null : num!);
                  }}
                  onBlur={(e) => {
                    const v = e.target.value.trim();
                    if (onlineBookingSaving) return;
                    handleOnlineBookingCapacityPercentChange(v);
                  }}
                  disabled={!tenantId || onlineBookingLoading}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 disabled:opacity-50"
                />
                <span className="text-sm text-muted-foreground whitespace-nowrap">%</span>
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                {onlineBookingLoading
                  ? 'Φόρτωση...'
                  : onlineBookingCapacityPercent != null
                    ? `${onlineBookingCapacityPercent}% της χωρητικότητας για online, ${100 - onlineBookingCapacityPercent}% για walk-in`
                    : 'Χωρίς όριο - όλη η χωρητικότητα διαθέσιμη online. Αφήστε κενό για χωρίς όριο.'}
              </p>
            </div>

            <div className="max-w-xs">
              <label htmlFor="maxOnlinePartySize" className="block text-sm font-medium text-muted-foreground mb-1">
                Μέγιστος αριθμός ατόμων για online κράτηση
              </label>
              <div className="flex items-center gap-2">
                <input
                  id="maxOnlinePartySize"
                  type="number"
                  min={1}
                  step="1"
                  placeholder="Προεπιλογή 10"
                  value={maxOnlinePartySize ?? ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    const num = v === '' ? null : parseInt(v, 10);
                    if (v !== '' && (!Number.isFinite(num) || num! < 1)) return;
                    setMaxOnlinePartySize(v === '' ? null : num!);
                  }}
                  onBlur={(e) => {
                    if (maxOnlinePartySizeSaving) return;
                    handleMaxOnlinePartySizeChange(e.target.value);
                  }}
                  disabled={!tenantId || onlineBookingLoading}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 disabled:opacity-50"
                />
                <span className="text-sm text-muted-foreground whitespace-nowrap">άτομα</span>
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                Αν δεν οριστεί τιμή, χρησιμοποιείται προεπιλογή 10 ατόμων.
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                Για μεγαλύτερες παρέες, ο πελάτης μπορεί να το αναφέρει στις σημειώσεις της κράτησης.
              </p>
            </div>
          </div>
        </div>
        
        {/* Tables Management */}
        <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 px-1 bg-purple-100 text-purple-600 rounded-lg flex items-center justify-center">
                <UtensilsCrossed className="h-4 w-4" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-card-foreground">Διαχείριση Τραπεζιών</h2>
                <p className="text-sm text-muted-foreground">
                  Προσθέστε ή επεξεργαστείτε τη διάταξη τραπεζιών και τις ζώνες τους.
                </p>
              </div>
            </div>
            <Button onClick={() => openCreateTable()} className="gap-2">
              <Plus className="h-4 w-4" />
              Νέο τραπέζι
            </Button>
          </div>

          <div className="mt-4 border-t border-border pt-4 space-y-3">
            {tablesLoading ? (
              <div className="flex items-center justify-center py-6 text-sm text-muted-foreground">
                Φόρτωση τραπεζιών...
              </div>
            ) : tables.length === 0 ? (
              <div className="rounded-md border border-dashed border-border py-6 text-center text-sm text-muted-foreground">
                Δεν έχουν καταχωρηθεί ακόμη τραπέζια.
              </div>
            ) : (
              <div className="grid gap-3">
                {tables
                  .slice()
                  .sort((a, b) => a.number - b.number)
                  .map((table) => (
                    <div
                      key={table.id}
                      className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border border-border rounded-lg px-4 py-3"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-primary/5 text-primary rounded-lg flex items-center justify-center font-semibold">
                          Τ{table.number}
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-card-foreground">
                            {table.zone}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {table.seats} {table.seats === 1 ? 'θέση' : 'θέσεις'}
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-wrap items-center justify-between gap-2 sm:gap-3">
                        {renderTableStatus(table.status)}
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            className="gap-2"
                            onClick={() => openEditTable(table)}
                          >
                            <Edit className="h-4 w-4" />
                            Επεξεργασία
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="gap-2 text-destructive border-destructive"
                            onClick={() => handleDeleteTable(table)}
                          >
                            <Trash2 className="h-4 w-4" />
                            Διαγραφή
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>
        </div>

        {/* Zones Management */}
        <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 px-1 bg-emerald-100 text-emerald-700 rounded-lg flex items-center justify-center">
                <span className="text-xs font-semibold">Ζ</span>
              </div>
              <div>
                <h2 className="text-lg font-semibold text-card-foreground">Διαχείριση Ζωνών</h2>
                <p className="text-sm text-muted-foreground">
                  Προβάλετε, μετονομάστε ή οργανώστε τις ζώνες του εστιατορίου.
                </p>
              </div>
            </div>
            <Button
              onClick={handleStartAddZone}
              className="gap-2"
              size="sm"
            >
              <Plus className="h-4 w-4" />
              Νέα ζώνη
            </Button>
          </div>

          {isAddingZone && (
            <div className="mt-3 rounded-lg border border-border p-3 bg-background/60">
              <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                Όνομα νέας ζώνης
              </label>
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="text"
                  value={newZoneDraft}
                  onChange={(e) => setNewZoneDraft(e.target.value)}
                  placeholder="π.χ. Κήπος"
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground"
                />
                <div className="flex gap-2">
                  <Button size="sm" onClick={handleConfirmAddZone}>
                    Δημιουργία
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setIsAddingZone(false);
                      setNewZoneDraft('');
                    }}
                  >
                    Ακύρωση
                  </Button>
                </div>
              </div>
            </div>
          )}

          {zonesWithCounts.length === 0 ? (
            <div className="mt-4 rounded-md border border-dashed border-border py-6 text-center text-sm text-muted-foreground">
              Δεν έχουν οριστεί ακόμη ζώνες. Δημιουργήστε μία προσθέτοντας τραπέζι με τιμή στο πεδίο «Ζώνη».
            </div>
          ) : (
            <div className="mt-4 border-t border-border pt-4 space-y-2">
              {zonesWithCounts.map(([zone, count]) => (
                <div
                  key={zone}
                  className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 border border-border rounded-lg px-4 py-3"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-8 h-8 bg-background border border-border rounded-lg flex items-center justify-center text-xs font-semibold">
                      {zone || '—'}
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-card-foreground truncate">
                        {zone || 'Χωρίς όνομα'}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {count} τραπέζ{count === 1 ? 'ι' : 'ια'} σε αυτή τη ζώνη
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-1"
                      onClick={() => {
                        setZoneBeingRenamed(zone);
                        setRenameZoneDraft(zone);
                      }}
                    >
                      Μετονομασία
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-1 text-muted-foreground border-dashed border-border"
                      onClick={() => {
                        const inUse = tables.some(
                          (t) => (t.zone || '').trim() === (zone || '').trim()
                        );
                        if (inUse) {
                          toast.error(
                            'Δεν μπορείτε να διαγράψετε ζώνη που χρησιμοποιείται. Μετακινήστε ή διαγράψτε πρώτα τα τραπέζια της.'
                          );
                        }
                      }}
                    >
                      Διαγραφή
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {zoneBeingRenamed && (
            <div className="mt-3 rounded-lg border border-border p-3 bg-background/60">
              <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                Μετονομασία ζώνης: {zoneBeingRenamed}
              </label>
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="text"
                  value={renameZoneDraft}
                  onChange={(e) => setRenameZoneDraft(e.target.value)}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground"
                />
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={async () => {
                      await handleRenameZone(zoneBeingRenamed, renameZoneDraft);
                      setZoneBeingRenamed(null);
                      setRenameZoneDraft('');
                    }}
                  >
                    Αποθήκευση
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setZoneBeingRenamed(null);
                      setRenameZoneDraft('');
                    }}
                  >
                    Ακύρωση
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 px-1 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center">
                <Clock className="h-4 w-4" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-card-foreground">Διαχείριση Βαρδιών</h2>
                <p className="text-sm text-muted-foreground">
                  Ορίστε τα ωράρια εξυπηρέτησης και ενεργοποιήστε/απενεργοποιήστε βάρδιες.
                </p>
              </div>
            </div>
            <Button onClick={openCreateShift} className="gap-2">
              <Plus className="h-4 w-4" />
              Νέα βάρδια
            </Button>
          </div>

          <div className="mt-4 border-t border-border pt-4 space-y-3">
            {shiftsLoading ? (
              <div className="flex items-center justify-center py-6 text-sm text-muted-foreground">
                Φόρτωση βαρδιών...
              </div>
            ) : shifts.length === 0 ? (
              <div className="rounded-md border border-dashed border-border py-6 text-center text-sm text-muted-foreground">
                Δεν έχουν καταχωρηθεί ακόμη βάρδιες.
              </div>
            ) : (
              <div className="grid gap-3">
                {shifts
                  .slice()
                  .sort((a, b) => a.start_time.localeCompare(b.start_time))
                  .map((shift) => (
                    <div
                      key={shift.id}
                      className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border border-border rounded-lg px-4 py-3"
                    >
                      <div>
                        <div className="text-sm font-semibold text-card-foreground">
                          {shift.name}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {shift.start_time} - {shift.end_time}
                        </div>
                      </div>

                      <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${
                            shift.is_active
                              ? 'bg-emerald-100 text-emerald-600'
                              : 'bg-slate-200 text-slate-600'
                          }`}
                        >
                          {shift.is_active ? 'Ενεργή' : 'Ανενεργή'}
                        </span>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleToggleShiftActive(shift)}
                          >
                            {shift.is_active ? 'Απενεργοποίηση' : 'Ενεργοποίηση'}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="gap-2"
                            onClick={() => openEditShift(shift)}
                          >
                            <Edit className="h-4 w-4" />
                            Επεξεργασία
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="gap-2 text-destructive border-destructive"
                            onClick={() => handleDeleteShift(shift)}
                          >
                            <Trash2 className="h-4 w-4" />
                            Διαγραφή
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>
        </div>

      </div>

      {/* Footer Info */}
      <div className="text-center pt-8 border-t border-border">
        <p className="text-xs text-muted-foreground">
          {APP_NAME} Restaurant Management System
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          Έκδοση {APP_VERSION}
        </p>
        {typeof __BUILD_AT__ !== 'undefined' && (
          <p className="text-xs text-muted-foreground mt-0.5" title="Χρησιμοποιήστε για να επιβεβαιώσετε ότι έχετε την τελευταία ανάπτυξη">
            Build: {new Date(__BUILD_AT__).toLocaleString('el-GR')}
          </p>
        )}
      </div>

      <TableForm
        open={tableModalOpen}
        onClose={closeTableModal}
        onSubmit={handleSaveTable}
        initialValues={editingTable}
        isSubmitting={tableSubmitting}
        existingZones={zoneSuggestions}
        initialZone={defaultZoneForNewTable}
        maxSeatsLimit={maxTableSeats}
      />
      <ShiftForm
        open={shiftModalOpen}
        onClose={closeShiftModal}
        onSubmit={handleSaveShift}
        initialValues={editingShift}
        isSubmitting={shiftSubmitting}
      />
    </div>
  );
};

export default Settings;
